import type { NameValue } from './types.js';

/**
 * Подстановка значений в запрос по плейсхолдерам вида {ключ}.
 *
 * Перенос insertParameterValue из CommonVars.kt. Отличие от оригинала: карта значений
 * передаётся аргументом, а не берётся из глобальной переменной rowFieldsValueMap —
 * глобальное состояние в TS-версии живёт в store (фронтенд) и в контексте запроса (бэкенд).
 *
 * Поведение сохранено: если в строке нет '{', карта не просматривается вовсе;
 * ключи без плейсхолдера в тексте просто не находят совпадений.
 */
export function insertParameterValue(
  queryIn: string,
  fieldsValueMap: Record<string, string> = {},
): string {
  if (!queryIn.includes('{')) return queryIn;

  let query = queryIn;
  for (const [key, value] of Object.entries(fieldsValueMap)) {
    query = query.replaceAll(`{${key}}`, value);
  }
  return query;
}

/**
 * Разбор строки «имя:значение» из описания колонки (аналог nameValue из CommonVars.kt).
 *
 * Делит по ПЕРВОМУ вхождению разделителя, обе части обрезает по краям.
 * Поведение при отсутствии разделителя сохранено от оригинала: в Kotlin
 * substring(0, indexOf(':')) бросал исключение — здесь тоже бросаем, чтобы расхождение
 * с оригиналом не прошло молча (в fullWeb такие строки в columns отсекаются фильтром).
 */
export function parseNameValue(nameAndValue: string, delimiter = ':'): NameValue {
  const index = nameAndValue.indexOf(delimiter);
  if (index < 0) {
    throw new Error(
      `parseNameValue: разделитель "${delimiter}" не найден в строке "${nameAndValue}"`,
    );
  }
  return {
    name: nameAndValue.slice(0, index).trim(),
    value: nameAndValue.slice(index + delimiter.length).trim(),
  };
}

/**
 * Признак первичного ключа строки (аналог idFieds из PgSQL.kt).
 *
 * Оригинал определял ключ эвристикой: имя `id` или оканчивается на `_id`, а значение —
 * не строка (в JSON приходит без кавычек). Здесь та же логика, но по разобранному JSON.
 * Переход на реальные ключи из information_schema — фаза 6.
 */
export function isPrimaryKeyField(name: string, value: unknown): boolean {
  if (typeof value === 'string') return false;
  return name === 'id' || name.endsWith('_id');
}
